import React, { useEffect } from 'react';
import { View, Text, FlatList } from 'react-native';
import usePlacesAutocomplete from 'use-places-autocomplete';
import { styles } from './AutoComplete.styles';
import InputField from '../input/input';
import { Address } from '@typeRoots/common/address';
import { formatAddressToString, mapFetchApi } from 'src/helpers/addressHelper';
import { RootState } from 'src/store/store';
import { useSelector } from 'react-redux';
export interface Props {
  setMapAddress: (address: Address) => void;
  mapAddress: Address;
  testID?: string;
}
const PlacesAutocompleteComponent: React.FC<Props> = ({ setMapAddress, mapAddress, testID }) => {
  const token = useSelector((state: RootState) => state.users.userToken); // Get the token from Redux store
  const tpJwt = useSelector((state: RootState) => state.UI.tpJwtToken); // Get the token from Redux store
  useEffect(() => {
    //Sync marker position with initial position when the map is first loaded
    setValue(
      mapAddress.street && mapAddress.street.length > 0 ? formatAddressToString(mapAddress) : '',
      false,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapAddress]);
  // usePlacesAutocomplete hook
  const {
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      location: new google.maps.LatLng(40.7128, -74.006), // Coordinates for New York
      radius: 200 * 1000, // Set search radius (200 km)
      componentRestrictions: { country: 'us' }, // Restrict results to USA
    },
    debounce: 300, // Debouncing the input to optimize API calls
  });

  // Handle input changes
  const handleInputChange = (text: string) => {
    setValue(text); // Update the value in the hook
  };

  // Handle item selection from suggestions
  const handleSelect = async (address: string, placeId: string) => {
    setValue(address, false); // Set selected address
    // Fetch the coordinates (lat, lng) for the selected place using the place_id
    const url = token
      ? `${process.env.REACT_APP_API_BASE_URL}/maps/geocode/place?placeId=${placeId}`
      : `${process.env.REACT_APP_API_BASE_URL}/auth/place?placeId=${placeId}&tpJwt=${tpJwt}`;

    const geocodeResult = await mapFetchApi(token, url);

    const geocodeData = await geocodeResult.json();

    if (geocodeData.status === 'OK' && geocodeData.results.length > 0) {
      const location = geocodeData.results[0].geometry.location;
      const position = { lat: location.lat, lng: location.lng };

      // Dispatch to update the map address and position
      setMapAddress({
        ...mapAddress,
        street: address,
        latitude: position.lat,
        longitude: position.lng,
      });
    } else {
      // If the status is not OK, print the error details
      console.error('Failed to fetch geocode data, reason: ', geocodeData.status);
      console.error('Error message: ', geocodeData.error_message || 'No error message');
    }

    clearSuggestions(); // Clear suggestions after selection
  };

  return (
    <View style={styles.container}>
      <InputField
        variant="caption"
        label={''}
        value={value}
        onChange={handleInputChange}
        testID={`${testID}-input-field`}
      />
      {/* Show suggestions if status is OK */}
      {status === 'OK' && (
        <FlatList
          data={data}
          keyExtractor={(item) => item.place_id} // Unique key for each suggestion
          renderItem={({ item }) => (
            <Text
              testID={`${testID}-${item.place_id}`}
              style={styles.suggestion}
              onPress={() => handleSelect(item.description, item.place_id)} // Handle selection
            >
              {item.description} {/* Displaying the suggestion */}
            </Text>
          )}
        />
      )}
    </View>
  );
};

export default PlacesAutocompleteComponent;
